import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * ChatGPT Integration for Smart Bio Searching
 *
 * This utility provides intelligent bio matching by delegating OpenAI calls to
 * Firebase Cloud Functions so sensitive keys remain on the server.
 */

const functions = getFunctions();

/**
 * Analyzes user bios against a search query using ChatGPT (via Firebase functions)
 * @param {string} searchQuery - The user's search input
 * @param {Array} userBios - Array of objects with { userId, bio, username, userType }
 * @returns {Promise<Array>} - Array of matched user IDs with relevance scores
 */
export const analyzeUserBiosWithChatGPT = async (searchQuery, userBios) => {
  try {
    const performChatGPTBioAnalysis = httpsCallable(functions, 'performChatGPTBioAnalysis');
    const response = await performChatGPTBioAnalysis({
      searchQuery,
      userBios,
    });

    const results = response?.data?.results;

    if (Array.isArray(results)) {
      return results;
    }

    throw new Error('Invalid response format from performChatGPTBioAnalysis');
  } catch (error) {
    console.error('ChatGPT bio analysis failed:', error);
    // Fallback to basic search if the cloud function fails
    return fallbackBioSearch(searchQuery, userBios);
  }
};

/**
 * Fallback search when ChatGPT is unavailable
 */
const fallbackBioSearch = (searchQuery, userBios) => {
  const query = searchQuery.toLowerCase();
  
  return userBios
    .map(user => {
      let score = 0;
      const bio = user.bio?.toLowerCase() || '';
      const username = user.username?.toLowerCase() || '';
      const userType = user.userType?.toLowerCase() || '';

      // Basic keyword matching
      if (bio.includes(query)) score += 0.8;
      if (username.includes(query)) score += 0.9;
      if (userType.includes(query)) score += 0.7;

      // Word-by-word matching for partial matches
      const queryWords = query.split(' ').filter(word => word.length > 2);
      queryWords.forEach(word => {
        if (bio.includes(word)) score += 0.3;
        if (username.includes(word)) score += 0.4;
        if (userType.includes(word)) score += 0.3;
      });

      return {
        userId: user.userId,
        score: Math.min(score, 1.0),
        reason: 'Basic keyword match'
      };
    })
    .filter(result => result.score >= 0.6)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
};

/**
 * Batch process users for ChatGPT analysis (to handle large datasets)
 */
export const batchAnalyzeUserBios = async (searchQuery, allUsers, batchSize = 20) => {
  const results = [];
  
  for (let i = 0; i < allUsers.length; i += batchSize) {
    const batch = allUsers.slice(i, i + batchSize);
    const batchResults = await analyzeUserBiosWithChatGPT(searchQuery, batch);
    results.push(...batchResults);
  }

  // Combine and re-sort all results (don't limit - return all analyzed results)
  return results.sort((a, b) => b.score - a.score);
};

/**
 * Enhanced search that combines Firestore queries with ChatGPT analysis
 */
export const performEnhancedCommunitySearch = async (searchQuery, firestoreUsers) => {
  console.log('💬 performEnhancedCommunitySearch: Firestore users', firestoreUsers);
  // Prepare user data for ChatGPT analysis
  const userBiosForAnalysis = firestoreUsers
    // .filter(user => user.bio && user.bio.trim().length > 10) // Only analyze users with substantial bios
    .map(user => ({
      userId: user.id,
      bio: user.bio || '',
      username: user.username || '',
      userType: user.userType || 'member',
      title: user.title || '',
      accountType: user.accountType || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      location: user.location || '',
    }));

  if (userBiosForAnalysis.length === 0) {
    // No bios to analyze, return original results
    return firestoreUsers;
  }

  try {
    // Get ChatGPT analysis results
    console.log("Analyzing: ", userBiosForAnalysis);
    const chatGPTResults = await batchAnalyzeUserBios(searchQuery, userBiosForAnalysis);
    console.log('💬 performEnhancedCommunitySearch: ChatGPT results', chatGPTResults);
    
    // Create a score map
    const scoreMap = new Map();
    chatGPTResults.forEach(result => {
      scoreMap.set(result.userId, result.score);
    });

    // Enhance original results with ChatGPT scores (keyed by user.id)
    const enhancedResults = firestoreUsers.map(user => {
      const relevance = scoreMap.get(user.id) || 0;
      const result = chatGPTResults.find(r => r.userId === user.id);
      return {
        ...user,
        relevanceScore: relevance,
        matchReason: result?.reason || ''
      };
    });


    // Sort by relevance score, then maintain original order if equal
    return enhancedResults.sort((a, b) => {
      if (b.relevanceScore !== a.relevanceScore) {
        return b.relevanceScore - a.relevanceScore;
      }
      return 0; // Maintain original order for equal scores
    });

  } catch (error) {
    console.error('Enhanced search failed, returning original results:', error);
    return firestoreUsers;
  }
};

export default {
  analyzeUserBiosWithChatGPT,
  batchAnalyzeUserBios,
  performEnhancedCommunitySearch
};
